import { DistanceUnitEnum, ScoreTypeEnum, WeightUnitEnum } from 'types/enums';

import { SectionMovement, SectionMovementScoreSet, WorkoutSectionData } from './types';

/**
 * Predicate for filtering form movements that are ready to be submitted.
 * Form validation (validateMovementScoring) guarantees movements with a movementId
 * also have a non-null scoreType, but write-payload builders apply this filter as
 * a defense-in-depth so partially-filled rows are silently dropped rather than
 * sent to the backend.
 */
export const hasMovementScoring = (m: SectionMovement): boolean =>
  Boolean(m.movementId) && m.scoreType !== null;

interface MovementScoringPayload {
  scoreType: ScoreTypeEnum;
  distanceUnit: DistanceUnitEnum | null;
  weightUnit: WeightUnitEnum | null;
  scoreSets: SectionMovementScoreSet[];
}

/**
 * Extract the scoring portion of a backend write payload from a form-side
 * SectionMovement. Callers must pre-filter with hasMovementScoring so the
 * non-null assertion on scoreType is safe.
 */
export const extractMovementScoringPayload = (m: SectionMovement): MovementScoringPayload => ({
  scoreType: m.scoreType as ScoreTypeEnum,
  distanceUnit: m.distanceUnit,
  weightUnit: m.scoreType === ScoreTypeEnum.WEIGHT_SETS_REPS ? m.weightUnit : null,
  scoreSets: m.scoreType === ScoreTypeEnum.WEIGHT_SETS_REPS ? m.scoreSets : [],
});

interface MovementScoringValidateOptions {
  // Set true for section-level mode where movementId is intentionally empty until save.
  skipMovementIdGuard?: boolean;
}

/**
 * Validate scoring config on a single form-side movement. Reports each rule via `setFieldError`.
 * Returns true when a new error was set so the caller can flip its own hasErrors flag.
 */
export function validateMovementScoring(
  movement: SectionMovement,
  basePath: string,
  setFieldError: (path: string, message: string) => void,
  { skipMovementIdGuard = false }: MovementScoringValidateOptions = {},
): boolean {
  if (!skipMovementIdGuard && !movement.movementId) return false;

  if (!movement.scoreType) {
    setFieldError(`${basePath}.scoreType`, 'Scoring type is required');
    return true;
  }

  if (movement.scoreType === ScoreTypeEnum.WEIGHT_SETS_REPS) {
    if (movement.scoreSets.length === 0) {
      setFieldError(`${basePath}.scoreSets`, 'At least one set is required');
      return true;
    }
    if (movement.scoreSets.some(s => s.targetReps < 1)) {
      setFieldError(`${basePath}.scoreSets`, 'Every set must have at least 1 target rep');
      return true;
    }
  }

  return false;
}

/** Validate the score config for a section-level section (stored on movements[0]). */
export function validateSectionScoring(
  section: WorkoutSectionData,
  basePath: string,
  setFieldError: (path: string, message: string) => void,
): boolean {
  const m = section.movements[0];
  if (!m) {
    setFieldError(`${basePath}.movements.0.scoreType`, 'Scoring type is required');
    return true;
  }
  return validateMovementScoring(m, `${basePath}.movements.0`, setFieldError, {
    skipMovementIdGuard: true,
  });
}

/**
 * Validate every section + movement, setting inline field errors AND returning a
 * path -> message map of everything that failed. Wrapping `setFieldError` lets us
 * capture each message for an aggregate summary without changing the scoring
 * validators' signatures. Returns an empty object when the form is valid.
 */
export function collectSectionErrors(
  sections: WorkoutSectionData[],
  setFieldError: (path: string, message: string) => void,
): Record<string, string> {
  const collected: Record<string, string> = {};
  const record = (path: string, message: string): void => {
    collected[path] = message;
    setFieldError(path, message);
  };

  sections.forEach((section, sectionIndex) => {
    if (!section.name?.trim()) {
      record(`sections.${sectionIndex}.name`, 'Section name is required');
    }
    if (section.scoreEntireSection) {
      validateSectionScoring(section, `sections.${sectionIndex}`, record);
    } else {
      section.movements.forEach((movement, movementIndex) => {
        validateMovementScoring(movement, `sections.${sectionIndex}.movements.${movementIndex}`, record);
      });
    }
  });

  return collected;
}
